import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import { MatchCommentEventColor } from '../../../typings/design-tokens';
import { IComponent } from '../../../typings/generic-component';
import { MatchCommentEvent } from '../../../typings/match-comments';
import { RMark } from '../../../utils/markdown';
import { getMatchEventData } from '../../../utils/match-comments';
import { makeClassNames } from '../../../utils/styles';
import { IIcon } from '../../atoms/icons/icon';
import { isPropTypeNullable } from '../../../utils/prop-types';

import './match-comment.scss';

export interface IMatchComment extends IComponent {
  commentId: string;
  message?: string;
  time: string;
  type: MatchCommentEvent;
}

export const MatchCommentPropTypes = {
  commentId: PropTypes.string.isRequired,
  message: isPropTypeNullable(PropTypes.string),
  time: PropTypes.string.isRequired,
  type: PropTypes.any.isRequired,
};

interface IMatchCommentClasses {
  matchComment: string;
  timestamp: string;
  wrapper: string;
  container: string;
  content: string;
  eventContainer: string;
  body: string;
}
// using ISimplePaginationClasses instead of IClassNames for better IDE intellisense

const classNames = (
  type?: unknown,
  color?: MatchCommentEventColor | null,
  iconColor?: MatchCommentEventColor | null,
  isGol?: boolean
): IMatchCommentClasses =>
  makeClassNames({
    matchComment: 'match-comment',
    timestamp: 'match-comment__timestamp',
    wrapper: 'match-comment__wrapper',
    container: 'match-comment__container',
    content: [
      type && 'match-comment__event-post-content',
      Boolean(color) && `match-comment__event__${color}`,
      isGol && ' match-comment__event-post-content-gol match-comment__content-gol',
      'match-comment__content',
    ],
    body: [isGol && 'match-comment__body-gol', 'match-comment__body'],
    eventContainer: [
      'match-comment__event__container',
      Boolean(iconColor) && `match-comment__event__icon-${iconColor}`,
    ],
  }) as IMatchCommentClasses;
const MatchComment: React.FC<IMatchComment> = ({ commentId, message = '', time, type }) => {
  const isGol: boolean = ['goal', 'penalty goal'].includes(type); // type === events?.goal.text;

  const [eventColor, iconColor, eventIcon] = useMemo<
    [MatchCommentEventColor | null, MatchCommentEventColor | null, ((props: IIcon) => React.ReactElement) | null]
  >(() => {
    const eventData = getMatchEventData(type);
    return [eventData?.color ?? null, eventData?.iconColor ?? null, eventData?.icon ?? null];
  }, [type]);

  const cn = classNames(type, eventColor, iconColor, isGol);

  return (
    <div id={`match-comment-${commentId}`} className={cn.matchComment}>
      <div className={cn.timestamp}>
        <time dateTime={time}>{time}</time>
      </div>
      <div className={cn.wrapper}>
        <div className={cn.container}>
          <div className={cn.content}>
            {type && eventIcon && (
              <div className={cn.eventContainer}>{eventIcon({ customSize: { width: '24', height: '24' } })}</div>
            )}
            <div className={cn.body} dangerouslySetInnerHTML={{ __html: new RMark().render(message) }} />
          </div>
        </div>
      </div>
    </div>
  );
};

if (process.env.NODE_ENV !== 'production') {
  MatchComment.propTypes = MatchCommentPropTypes;
}

export default MatchComment;
